import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc,
  setDoc,
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

// ============================================
// JOBS COLLECTION
// ============================================

export const jobsCollection = collection(db, 'jobs');

export const getAllJobs = async () => {
  try {
    const querySnapshot = await getDocs(
      query(jobsCollection, orderBy('order', 'asc'))
    );
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting jobs:', error);
    throw error;
  }
};

export const getJobById = async (id) => {
  try {
    const docRef = doc(db, 'jobs', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting job:', error);
    throw error;
  }
};

export const createJob = async (jobData) => {
  try {
    const docRef = await addDoc(jobsCollection, {
      ...jobData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating job:', error);
    throw error;
  }
};

export const updateJob = async (id, jobData) => {
  try {
    const docRef = doc(db, 'jobs', id);
    await updateDoc(docRef, {
      ...jobData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating job:', error);
    throw error;
  }
};

export const deleteJob = async (id) => {
  try {
    await deleteDoc(doc(db, 'jobs', id));
  } catch (error) {
    console.error('Error deleting job:', error);
    throw error;
  }
};

// ============================================
// NEWS COLLECTION
// ============================================

export const newsCollection = collection(db, 'news');

export const getAllNews = async (limitCount = null) => {
  try {
    let q = query(newsCollection, orderBy('date', 'desc'));
    if (limitCount) {
      q = query(q, limit(limitCount));
    }
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting news:', error);
    throw error;
  }
};

export const getNewsById = async (id) => {
  try {
    const docRef = doc(db, 'news', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting news item:', error);
    throw error;
  }
};

export const createNews = async (newsData) => {
  try {
    const docRef = await addDoc(newsCollection, {
      ...newsData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating news:', error);
    throw error;
  }
};

export const updateNews = async (id, newsData) => {
  try {
    const docRef = doc(db, 'news', id);
    await updateDoc(docRef, {
      ...newsData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating news:', error);
    throw error;
  }
};

export const deleteNews = async (id) => {
  try {
    await deleteDoc(doc(db, 'news', id));
  } catch (error) {
    console.error('Error deleting news:', error);
    throw error;
  }
};

// ============================================
// PEOPLE COLLECTION
// ============================================

export const peopleCollection = collection(db, 'people');

export const getAllPeople = async () => {
  try {
    const querySnapshot = await getDocs(
      query(peopleCollection, orderBy('order', 'asc'))
    );
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting people:', error);
    throw error;
  }
};

export const getPersonById = async (id) => {
  try {
    const docRef = doc(db, 'people', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting person:', error);
    throw error;
  }
};

export const createPerson = async (personData) => {
  try {
    const docRef = await addDoc(peopleCollection, {
      ...personData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating person:', error);
    throw error;
  }
};

export const updatePerson = async (id, personData) => {
  try {
    const docRef = doc(db, 'people', id);
    await updateDoc(docRef, {
      ...personData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating person:', error);
    throw error;
  }
};

export const deletePerson = async (id) => {
  try {
    await deleteDoc(doc(db, 'people', id));
  } catch (error) {
    console.error('Error deleting person:', error);
    throw error;
  }
};

// ============================================
// INTERVIEWS COLLECTION
// ============================================

export const interviewsCollection = collection(db, 'interviews');

export const getAllInterviews = async () => {
  try {
    const querySnapshot = await getDocs(
      query(interviewsCollection, orderBy('order', 'asc'))
    );
    return querySnapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
  } catch (error) {
    console.error('Error getting interviews:', error);
    throw error;
  }
};

export const getInterviewBySlug = async (slug) => {
  try {
    const docRef = doc(db, 'interviews', slug);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting interview:', error);
    throw error;
  }
};

export const createInterview = async (interviewData) => {
  try {
    const slug = interviewData.slug;
    const docRef = slug ? doc(db, 'interviews', slug) : doc(interviewsCollection);
    await setDoc(docRef, {
      ...interviewData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating interview:', error);
    throw error;
  }
};

export const upsertInterview = async (interviewData, overwrite = false) => {
  try {
    const slug = interviewData.slug;
    if (!slug) {
      throw new Error('Interview slug is required');
    }
    const docRef = doc(db, 'interviews', slug);
    const existing = await getDoc(docRef);
    if (existing.exists() && !overwrite) {
      return { id: existing.id, skipped: true };
    }
    await setDoc(docRef, {
      ...interviewData,
      createdAt: existing.exists() ? existing.data().createdAt : Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return { id: slug, skipped: false };
  } catch (error) {
    console.error('Error upserting interview:', error);
    throw error;
  }
};

export const updateInterview = async (id, interviewData) => {
  try {
    const docRef = doc(db, 'interviews', id);
    await updateDoc(docRef, {
      ...interviewData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating interview:', error);
    throw error;
  }
};

export const deleteInterview = async (id) => {
  try {
    await deleteDoc(doc(db, 'interviews', id));
  } catch (error) {
    console.error('Error deleting interview:', error);
    throw error;
  }
};

// ============================================
// ABOUT CONTENT COLLECTION
// ============================================

export const aboutCollection = collection(db, 'about');

export const getAllAboutContent = async () => {
  try {
    const querySnapshot = await getDocs(aboutCollection);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting about content:', error);
    throw error;
  }
};

export const getAboutContentByKey = async (key) => {
  try {
    const q = query(aboutCollection, where('key', '==', key));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting about content:', error);
    throw error;
  }
};

export const updateAboutContent = async (id, contentData) => {
  try {
    const docRef = doc(db, 'about', id);
    await updateDoc(docRef, {
      ...contentData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating about content:', error);
    throw error;
  }
};

// ============================================
// CULTURE CONTENT COLLECTION
// ============================================

export const cultureCollection = collection(db, 'culture');

export const getAllCultureContent = async () => {
  try {
    const querySnapshot = await getDocs(cultureCollection);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting culture content:', error);
    throw error;
  }
};

export const getCultureContentByKey = async (key) => {
  try {
    const q = query(cultureCollection, where('key', '==', key));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting culture content:', error);
    throw error;
  }
};

export const updateCultureContent = async (id, contentData) => {
  try {
    const docRef = doc(db, 'culture', id);
    await updateDoc(docRef, {
      ...contentData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating culture content:', error);
    throw error;
  }
};

// ============================================
// PAGES COLLECTION (for dynamic CMS pages)
// ============================================

export const pagesCollection = collection(db, 'pages');

export const getAllPages = async () => {
  try {
    const querySnapshot = await getDocs(
      query(pagesCollection, orderBy('updatedAt', 'desc'))
    );
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting pages:', error);
    throw error;
  }
};

export const getPageBySlug = async (slug) => {
  try {
    console.log('firestoreService: Querying for slug:', slug);
    const q = query(pagesCollection, where('slug', '==', slug), where('status', '==', 'published'));
    const querySnapshot = await getDocs(q);
    console.log('firestoreService: Query returned', querySnapshot.size, 'documents');
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = { id: doc.id, ...doc.data() };
      console.log('firestoreService: Found page:', data);
      return data;
    }
    
    console.log('firestoreService: No published page found for slug:', slug);
    return null;
  } catch (error) {
    console.error('Error getting page by slug:', error);
    throw error;
  }
};

export const getPageById = async (id) => {
  try {
    const docRef = doc(db, 'pages', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting page:', error);
    throw error;
  }
};

// Get page by slug (for seed data - checks all statuses)
export const getPageBySlugForSeed = async (slug) => {
  try {
    const q = query(pagesCollection, where('slug', '==', slug));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting page by slug:', error);
    throw error;
  }
};

export const createPage = async (pageData, overwrite = false) => {
  try {
    // Check if page with same slug already exists
    const existingPage = await getPageBySlugForSeed(pageData.slug);
    
    if (existingPage) {
      if (!overwrite) {
        throw new Error(`ページがすでに存在します: ${pageData.slug}`);
      }
      // Overwrite existing page
      const docRef = doc(db, 'pages', existingPage.id);
      await updateDoc(docRef, {
        ...pageData,
        updatedAt: Timestamp.now()
      });
      return existingPage.id;
    }
    
    // Create new page
    const docRef = await addDoc(pagesCollection, {
      ...pageData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating page:', error);
    throw error;
  }
};

export const updatePage = async (id, pageData) => {
  try {
    const docRef = doc(db, 'pages', id);
    await updateDoc(docRef, {
      ...pageData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating page:', error);
    throw error;
  }
};

export const deletePage = async (id) => {
  try {
    await deleteDoc(doc(db, 'pages', id));
  } catch (error) {
    console.error('Error deleting page:', error);
    throw error;
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Get all collections for admin overview
export const getAllCollections = async () => {
  const [jobs, news, people, interviews] = await Promise.all([
    getAllJobs().catch((error) => {
      console.warn('Error getting jobs:', error.message);
      return [];
    }),
    getAllNews().catch((error) => {
      console.warn('Error getting news:', error.message);
      return [];
    }),
    getAllPeople().catch((error) => {
      console.warn('Error getting people:', error.message);
      return [];
    }),
    getAllInterviews().catch((error) => {
      console.warn('Error getting interviews:', error.message);
      return [];
    })
  ]);

  return {
    jobs,
    news,
    people,
    interviews
  };
};

// Search functionality
export const searchContent = async (collectionName, searchTerm) => {
  try {
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);
    const results = querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(item => {
        const searchString = JSON.stringify(item).toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      });
    return results;
  } catch (error) {
    console.error('Error searching content:', error);
    throw error;
  }
};
